import { apiRequest, apiUpload } from "./apiClient";
import { rt } from "../i18n/runtime";
import { trackEngagementDataAdded } from "./engagementService";
import { isDataSyncConnected } from "./dataSyncService";
import { readSensitiveItem, removeSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

const chatPath = (familyId, suffix = "") => `/chat/${encodeURIComponent(familyId)}${suffix}`;
const roomPath = (familyId, roomId, suffix = "") => chatPath(familyId, `/rooms/${encodeURIComponent(roomId)}${suffix}`);
const messagePath = (familyId, roomId, mesajId, suffix = "") => roomPath(familyId, roomId, `/messages/${encodeURIComponent(mesajId)}${suffix}`);

const normaliseTs = (value) => value || null;

const normaliseRoom = (room = {}) => ({
  ...room,
  id: room.id || room.firebaseId,
  ad: room.ad || room.name || "Sohbet",
  uyeUidler: Array.isArray(room.uyeUidler) ? room.uyeUidler : [],
  createdAt: normaliseTs(room.createdAt),
  updatedAt: normaliseTs(room.updatedAt),
  lastMessageAt: normaliseTs(room.lastMessageAt),
  okunmamisSayac: room.okunmamisSayac || {},
  avatarUrl: room.avatarUrl || room.avatar || null,
  avatarFileKey: room.avatarFileKey || null,
  autoSaveMedia: !!room.autoSaveMedia,
});

const normaliseMessage = (message = {}) => ({
  ...message,
  id: message.id || message.firebaseId,
  metin: message.metin ?? "",
  type: message.type || "text",
  createdAt: normaliseTs(message.createdAt),
  reactions: message.reactions || {},
  readBy: Array.isArray(message.readBy) ? message.readBy : [],
  pinned: !!message.pinned,
  edited: !!message.edited,
  deletedAt: normaliseTs(message.deletedAt),
  mediaExpired: !!message.mediaExpired,
  mediaDeletedAt: normaliseTs(message.mediaDeletedAt),
  mediaRetentionDays: message.mediaRetentionDays || 30,
  replyTo: message.replyTo || null,
  menuVotes: message.menuVotes || {},
  // MADDE 4: Oylama (poll) mesajlari. Menu karti ile birebir ayni desende
  // saklanir; backend metadata/raw_firebase_data icinde tasir.
  pollData: message.pollData || null,
  pollVotes: message.pollVotes || {},
  durationSeconds: Number(message.durationSeconds || message.audioDurationSeconds || 0),
});

const roomRefreshEvent = (familyId) => `fp:chatRoomsRefresh:${familyId}`;
const messageRefreshEvent = (familyId, roomId) => `fp:chatMessagesRefresh:${familyId}:${roomId}`;

// Sohbet cache'i: önce memory, uygulama kill sonrası da localStorage.
// Yetki kaynağı değişmez; her açılışta backend/PostgreSQL yine arkadan güncellenir.
const roomsCache = new Map();
const messagesCache = new Map();
const CHAT_CACHE_PREFIX = "familyPlanner.chatCache.v2";
const CHAT_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const MAX_CACHED_ROOMS = 40;
const MAX_CACHED_MESSAGES = 80;
// SSE bağlantısı yoksa kullanıcı deneyimini koruyan seyrek güvenlik ağı.
// Bağlantı varken periyodik chat GET'i çalışmaz.
const CHAT_DISCONNECTED_POLL_MS = 30000;

// Aynı kullanıcı/aile için useAppData ve ChatPage birlikte dinleme açabilir.
// Tek bir ortak polling döngüsü kullanılır; sonuç tüm abonelere dağıtılır.
// Böylece mevcut UI/callback yapısı değişmeden çift /rooms istekleri engellenir.
const sharedRoomStreams = new Map();
const ROOM_REFRESH_DEBOUNCE_MS = 350;

// active-session effect'i görünürlük/cleanup geçişlerinde aynı payload'ı
// çok kısa aralıkla birkaç kez üretebilir. Yalnızca birebir aynı kısa tekrarlar
// bastırılır; farklı active/room durumu ve 25 sn heartbeat aynen çalışır.
const activeSessionRequestState = new Map();
const ACTIVE_SESSION_DEDUPE_MS = 1500;

// Oda açılışı ve mesaj listesinin aynı anda güncellenmesi, aynı oda için
// kısa aralıkta birden fazla /read çağrısı üretebilir. Mevcut okundu
// davranışını değiştirmeden yalnızca paralel/birebir kısa tekrarları birleştirir.
const roomReadRequestState = new Map();
const ROOM_READ_DEDUPE_MS = 1500;

if (typeof window !== "undefined") {
  window.addEventListener("fp:userScopedStorageCleared", () => {
    roomsCache.clear();
    messagesCache.clear();
    activeSessionRequestState.clear();
    roomReadRequestState.clear();
  });
}

const roomsCacheKey = (familyId, userUid = "") => `${familyId || ""}:${userUid || ""}`;
const messagesCacheKey = (familyId, roomId, mesajLimit = null) => `${familyId || ""}:${roomId || ""}:${mesajLimit || "all"}`;
const storageKey = (type, key) => `${CHAT_CACHE_PREFIX}.${type}.${key}`;

const safeStorageRead = (type, key) => {
  if (typeof window === "undefined" || !window.localStorage || !key) return null;
  try {
    const raw = readSensitiveItem(storageKey(type, key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (Date.now() - Number(parsed.savedAt || 0) > CHAT_CACHE_TTL_MS) {
      removeSensitiveItem(storageKey(type, key));
      return null;
    }
    return parsed.items;
  } catch (_) {
    return null;
  }
};

const safeStorageWrite = (type, key, items, maxItems) => {
  if (typeof window === "undefined" || !window.localStorage || !key || !Array.isArray(items)) return;
  try {
    const compactItems = items.slice(-maxItems);
    writeSensitiveItem(storageKey(type, key), JSON.stringify({ savedAt: Date.now(), items: compactItems }));
  } catch (_) {}
};

const setCachedRooms = (familyId, userUid, rooms = []) => {
  const key = roomsCacheKey(familyId, userUid);
  const safeRooms = Array.isArray(rooms) ? rooms.slice(0, MAX_CACHED_ROOMS) : [];
  roomsCache.set(key, safeRooms);
  safeStorageWrite("rooms", key, safeRooms, MAX_CACHED_ROOMS);
};

const setCachedMessages = (familyId, roomId, mesajLimit, messages = []) => {
  const key = messagesCacheKey(familyId, roomId, mesajLimit);
  const safeMessages = Array.isArray(messages) ? messages.slice(-MAX_CACHED_MESSAGES) : [];
  messagesCache.set(key, safeMessages);
  safeStorageWrite("messages", key, safeMessages, MAX_CACHED_MESSAGES);
};

export const getCachedChatRooms = (familyId, userUid = "") => {
  const key = roomsCacheKey(familyId, userUid);
  if (roomsCache.has(key)) return roomsCache.get(key);
  const stored = safeStorageRead("rooms", key);
  if (stored) roomsCache.set(key, stored);
  return stored || null;
};

export const getCachedChatMessages = (familyId, roomId, mesajLimit = null) => {
  const key = messagesCacheKey(familyId, roomId, mesajLimit);
  if (messagesCache.has(key)) return messagesCache.get(key);
  const stored = safeStorageRead("messages", key);
  if (stored) messagesCache.set(key, stored);
  return stored || null;
};

const emitRoomsRefresh = (familyId) => {
  try { window.dispatchEvent(new Event(roomRefreshEvent(familyId))); } catch {}
};

const emitMessagesRefresh = (familyId, roomId) => {
  try { window.dispatchEvent(new Event(messageRefreshEvent(familyId, roomId))); } catch {}
  emitRoomsRefresh(familyId);
};

const emitChatDataChanged = (familyId, resource, action, item = null, itemId = null, extra = null) => {
  try {
    window.dispatchEvent(new CustomEvent("fp:dataChanged", {
      detail: { familyId: String(familyId || ""), resource, action, item, itemId, extra },
    }));
  } catch {}
};

const replaceOrAppend = (items, item) => {
  const normalizedId = item?.id;
  if (!normalizedId) return items;
  const exists = items.some((x) => x.id === normalizedId);
  return exists ? items.map((x) => (x.id === normalizedId ? item : x)) : [...items, item];
};

const publishMessageResponse = (familyId, roomId, data, action = "updated") => {
  const message = normaliseMessage(data?.message || {});
  if (message.id) {
    emitChatDataChanged(familyId, "chatMessages", action, message, message.id, {
      roomId,
      room: data?.room ? normaliseRoom(data.room) : null,
    });
  }
  return message;
};

export const odalariGetirService = async (familyId) => {
  const data = await apiRequest(chatPath(familyId, "/rooms"));
  return (data.rooms || []).map(normaliseRoom);
};

export const mesajlariGetirService = async (familyId, roomId, mesajLimit = null) => {
  const limitAktif = Number.isFinite(Number(mesajLimit)) && Number(mesajLimit) > 0;
  const qs = limitAktif ? `?limit=${Number(mesajLimit)}` : "";
  const data = await apiRequest(roomPath(familyId, roomId, `/messages${qs}`));
  return (data.messages || []).map(normaliseMessage);
};

/* REST polling: mevcut sayfa imzasını bozmaz; yazma sonrası event ile anında yenilenir. */
export const odalariDinleService = (familyId, userUid, setOdalar) => {
  if (!familyId || typeof setOdalar !== "function") return () => {};

  const streamKey = roomsCacheKey(familyId, userUid);
  let stream = sharedRoomStreams.get(streamKey);

  if (!stream) {
    stream = {
      familyId,
      userUid,
      subscribers: new Set(),
      disposed: false,
      inFlight: false,
      pendingRefresh: false,
      currentRooms: [],
      sseConnected: isDataSyncConnected(familyId),
      timer: null,
      debounceTimer: null,
      refresh: null,
      requestRefresh: null,
      onRoomRefresh: null,
      onVisible: null,
      onSseStatus: null,
      onDataChanged: null,
    };

    const broadcast = (odalar) => {
      stream.subscribers.forEach((subscriber) => {
        try { subscriber(odalar); } catch (_) {}
      });
    };

    stream.refresh = async () => {
      if (stream.disposed || stream.inFlight || !stream.familyId) {
        if (stream.inFlight) stream.pendingRefresh = true;
        return;
      }
      if (typeof document !== "undefined" && document.hidden) return;

      stream.inFlight = true;
      try {
        const odalar = await odalariGetirService(stream.familyId);
        stream.currentRooms = odalar;
        setCachedRooms(stream.familyId, stream.userUid, odalar);
        if (!stream.disposed) broadcast(odalar);
      } catch (error) {
        if (!stream.disposed) console.error("odalariDinle hata:", error);
      } finally {
        stream.inFlight = false;
        if (stream.pendingRefresh && !stream.disposed) {
          stream.pendingRefresh = false;
          stream.requestRefresh(true);
        }
      }
    };

    stream.requestRefresh = (immediate = false) => {
      if (stream.disposed) return;
      if (stream.debounceTimer) {
        clearTimeout(stream.debounceTimer);
        stream.debounceTimer = null;
      }
      if (immediate) {
        stream.refresh();
        return;
      }
      stream.debounceTimer = window.setTimeout(() => {
        stream.debounceTimer = null;
        stream.refresh();
      }, ROOM_REFRESH_DEBOUNCE_MS);
    };

    stream.onRoomRefresh = () => stream.requestRefresh(false);
    stream.onVisible = () => {
      if (!document.hidden) stream.requestRefresh(true);
    };
    const scheduleFallback = () => {
      if (stream.timer) clearInterval(stream.timer);
      stream.timer = null;
      if (!stream.sseConnected) {
        stream.timer = window.setInterval(() => stream.refresh(), CHAT_DISCONNECTED_POLL_MS);
      }
    };
    stream.onSseStatus = (event) => {
      const detail = event?.detail || {};
      if (String(detail.familyId || "") !== String(stream.familyId)) return;
      const connected = detail.connected === true;
      if (stream.sseConnected === connected) return;
      stream.sseConnected = connected;
      scheduleFallback();
    };
    stream.onDataReconcile = (event) => {
      if (String(event?.detail?.familyId || "") === String(stream.familyId)) {
        stream.requestRefresh(true);
      }
    };
    stream.onDataChanged = (event) => {
      const detail = event?.detail || {};
      if (String(detail.familyId || "") !== String(stream.familyId)) return;
      let room = detail.resource === "chatRooms" ? detail.item : detail.extra?.room;
      const roomId = detail.itemId || detail.extra?.roomId || room?.id;
      if (detail.resource === "chatRooms" && detail.action === "deleted" && roomId) {
        stream.currentRooms = stream.currentRooms.filter((entry) => String(entry.id) !== String(roomId));
      } else if (room) {
        room = normaliseRoom(room);
        stream.currentRooms = replaceOrAppend(stream.currentRooms, room)
          .sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime());
      } else {
        return;
      }
      setCachedRooms(stream.familyId, stream.userUid, stream.currentRooms);
      broadcast(stream.currentRooms);
    };

    window.addEventListener(roomRefreshEvent(familyId), stream.onRoomRefresh);
    window.addEventListener("fp:sseStatus", stream.onSseStatus);
    window.addEventListener("fp:dataChanged", stream.onDataChanged);
    window.addEventListener("fp:dataReconcile", stream.onDataReconcile);
    document.addEventListener("visibilitychange", stream.onVisible);
    scheduleFallback();
    sharedRoomStreams.set(streamKey, stream);
  }

  stream.subscribers.add(setOdalar);

  const cachedRooms = getCachedChatRooms(familyId, userUid);
  if (cachedRooms) {
    stream.currentRooms = cachedRooms;
    try { setOdalar(cachedRooms); } catch (_) {}
  }

  // İlk abone ortak stream'i oluşturduğunda anında yükler. Sonraki aboneler
  // mevcut cache'i kullanır; aynı anda ikinci ağ isteği başlatılmaz.
  if (stream.subscribers.size === 1) stream.requestRefresh(true);

  return () => {
    const current = sharedRoomStreams.get(streamKey);
    if (!current) return;

    current.subscribers.delete(setOdalar);
    if (current.subscribers.size > 0) return;

    current.disposed = true;
    if (current.timer) clearInterval(current.timer);
    if (current.debounceTimer) clearTimeout(current.debounceTimer);
    document.removeEventListener("visibilitychange", current.onVisible);
    window.removeEventListener(roomRefreshEvent(familyId), current.onRoomRefresh);
    window.removeEventListener("fp:sseStatus", current.onSseStatus);
    window.removeEventListener("fp:dataChanged", current.onDataChanged);
    window.removeEventListener("fp:dataReconcile", current.onDataReconcile);
    sharedRoomStreams.delete(streamKey);
  };
};

export const odaOlusturService = async ({ familyId, ad, uyeUidler, user, tamAd }) => {
  const data = await apiRequest(chatPath(familyId, "/rooms"), {
    method: "POST",
    body: JSON.stringify({ ad: ad?.trim(), uyeUidler, tamAd, olusturanUid: user?.uid }),
  });
  const room = normaliseRoom(data.room || {});
  trackEngagementDataAdded({ familyId, moduleKey: "chat", itemId: room.id, metadata: { source: "room_created" } });
  emitChatDataChanged(familyId, "chatRooms", "added", room, room.id);
  return room.id;
};

export const odaSilService = async ({ familyId, roomId }) => {
  await apiRequest(roomPath(familyId, roomId), { method: "DELETE" });
  emitChatDataChanged(familyId, "chatRooms", "deleted", null, roomId);
};

export const odaAdGuncelleService = async ({ familyId, roomId, yeniAd }) => {
  const data = await apiRequest(roomPath(familyId, roomId), {
    method: "PATCH",
    body: JSON.stringify({ ad: yeniAd?.trim() }),
  });
  const room = normaliseRoom(data.room || {});
  emitChatDataChanged(familyId, "chatRooms", "updated", room, room.id);
  return room;
};

export const uyeEkleService = async ({ familyId, roomId, uyeUid }) => {
  const data = await apiRequest(roomPath(familyId, roomId, "/members"), {
    method: "POST",
    body: JSON.stringify({ uyeUid }),
  });
  if (data?.room) emitChatDataChanged(familyId, "chatRooms", "updated", normaliseRoom(data.room), roomId);
};

export const odayaUyeEkleService = uyeEkleService;

export const mesajlariDinleService = (familyId, roomId, setMesajlar, mesajLimit = null, options = {}) => {
  let disposed = false;
  let inFlight = false;
  let timer = null;
  let sseConnected = isDataSyncConnected(familyId);
  let currentMessages = [];

  const cachedMessages = options?.emitCached === false
    ? null
    : getCachedChatMessages(familyId, roomId, mesajLimit);
  if (cachedMessages) {
    currentMessages = cachedMessages;
    try { setMesajlar(cachedMessages); } catch {}
  }

  const refresh = async () => {
    if (disposed || inFlight || !familyId || !roomId) return;
    // Arka plandayken snapshot yenilemesi atlanır. Öne dönünce anında bir kez
    // uzlaştırılır; canlı değişikliklerin ana kanalı mevcut tek SSE'dir.
    if (typeof document !== "undefined" && document.hidden) return;
    inFlight = true;
    try {
      const mesajlar = await mesajlariGetirService(familyId, roomId, mesajLimit);
      currentMessages = mesajlar;
      setCachedMessages(familyId, roomId, mesajLimit, mesajlar);
      if (!disposed) setMesajlar(mesajlar);
    } catch (error) {
      if (!disposed) console.error("mesajlariDinle hata:", error);
    } finally {
      inFlight = false;
    }
  };

  window.addEventListener(messageRefreshEvent(familyId, roomId), refresh);
  const onVisibleMsg = () => { if (!document.hidden) refresh(); };
  const scheduleFallback = () => {
    if (timer) clearInterval(timer);
    timer = null;
    if (!sseConnected) timer = window.setInterval(refresh, CHAT_DISCONNECTED_POLL_MS);
  };
  const onSseStatus = (event) => {
    const detail = event?.detail || {};
    if (String(detail.familyId || "") !== String(familyId)) return;
    const connected = detail.connected === true;
    if (sseConnected === connected) return;
    sseConnected = connected;
    scheduleFallback();
  };
  const onDataReconcile = (event) => {
    if (String(event?.detail?.familyId || "") === String(familyId)) refresh();
  };
  const onDataChanged = (event) => {
    const detail = event?.detail || {};
    if (detail.resource !== "chatMessages" || String(detail.familyId || "") !== String(familyId)) return;
    if (String(detail.extra?.roomId || "") !== String(roomId)) return;

    if (detail.item) {
      const message = normaliseMessage(detail.item);
      currentMessages = replaceOrAppend(currentMessages, message);
    } else if (detail.action === "deleted" && detail.itemId) {
      currentMessages = currentMessages.filter((message) => String(message.id) !== String(detail.itemId));
    } else if (detail.action === "read" && Array.isArray(detail.extra?.messageIds)) {
      const ids = new Set(detail.extra.messageIds.map(String));
      const readerUid = detail.extra?.userUid;
      currentMessages = currentMessages.map((message) => ids.has(String(message.id))
        ? { ...message, readBy: Array.from(new Set([...(message.readBy || []), readerUid].filter(Boolean))) }
        : message);
    } else {
      return;
    }
    currentMessages = currentMessages
      .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime())
      .slice(mesajLimit ? -Number(mesajLimit) : undefined);
    setCachedMessages(familyId, roomId, mesajLimit, currentMessages);
    if (!disposed) setMesajlar(currentMessages);
  };
  document.addEventListener("visibilitychange", onVisibleMsg);
  window.addEventListener("fp:sseStatus", onSseStatus);
  window.addEventListener("fp:dataChanged", onDataChanged);
  window.addEventListener("fp:dataReconcile", onDataReconcile);
  refresh();
  scheduleFallback();

  return () => {
    disposed = true;
    if (timer) clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibleMsg);
    window.removeEventListener(messageRefreshEvent(familyId, roomId), refresh);
    window.removeEventListener("fp:sseStatus", onSseStatus);
    window.removeEventListener("fp:dataChanged", onDataChanged);
    window.removeEventListener("fp:dataReconcile", onDataReconcile);
  };
};

const mesajPayload = ({ metin, user, tamAd, replyTo = null, ekstra = null }) => ({
  metin: (metin || "").trim(),
  uid: user?.uid,
  ad: tamAd,
  replyTo: replyTo
    ? { msgId: replyTo.id, id: replyTo.id, metin: replyTo.metin || "", ad: replyTo.ad || "", type: replyTo.type || "text", uid: replyTo.uid || null }
    : null,
  ...(ekstra || {}),
  type: ekstra?.type || "text",
});

export const mesajGonderService = async ({ familyId, roomId, metin, user, tamAd, replyTo = null, ekstra = null }) => {
  const body = mesajPayload({ metin, user, tamAd, replyTo, ekstra });
  body.summary =
    (metin || "").trim() ||
    (ekstra?.type === "menu" ? rt("chat.menuSuggestion") : "") ||
    // MADDE 4: Oylama mesajinin metni bos oldugu icin bildirim ozeti
    // oylama sorusundan uretilir ("📊 Bu aksam ne yiyelim?").
    (ekstra?.type === "poll" ? `📊 ${String(ekstra?.pollData?.soru || rt("chat.poll")).slice(0, 100)}` : "") ||
    "📎";
  const data = await apiRequest(roomPath(familyId, roomId, "/messages"), {
    method: "POST",
    body: JSON.stringify(body),
  });
  return publishMessageResponse(familyId, roomId, data, "added");
};

export const medyaGonderService = async ({ familyId, roomId, dosya, user, tamAd, replyTo = null, onProgress }) => {
  const formData = new FormData();
  formData.append("file", dosya);
  formData.append("type", "chat");
  formData.append("familyId", familyId);
  formData.append("userId", user?.uid || "system");
  const fileNameForType = String(dosya.name || "");
  const looksLikeVideo = String(dosya.type || "").startsWith("video/") || /\.(mov|mp4|m4v|webm)$/i.test(fileNameForType);
  if (looksLikeVideo) formData.append("subType", "video");

  const upload = await apiUpload("/files/upload", formData, { onProgress });
  const file = upload.file || upload || {};
  const resolvedFileUrl = file.fileUrl || file.url || file.downloadUrl || upload.fileUrl || upload.url || upload.downloadUrl || "";
  const resolvedFileKey = file.fileKey || upload.fileKey || null;
  const mimeType = file.mimeType || dosya.type || (looksLikeVideo ? "video/mp4" : "");
  const isImage = dosya.type?.startsWith("image/") || file.mimeType?.startsWith("image/");
  const isVideo = looksLikeVideo || dosya.type?.startsWith("video/") || file.mimeType?.startsWith("video/");
  const mediaMessageType = isImage ? "image" : (isVideo ? "video" : "file");
  const ozet = isImage ? rt("chat.photo") : (isVideo ? "🎬 Video" : `📎 ${dosya.name}`);

  const data = await apiRequest(roomPath(familyId, roomId, "/messages"), {
    method: "POST",
    body: JSON.stringify({
      metin: "",
      uid: user?.uid,
      ad: tamAd,
      type: mediaMessageType,
      mediaUrl: resolvedFileUrl,
      fileUrl: resolvedFileUrl,
      mediaType: mimeType,
      fileName: file.fileName || dosya.name,
      fileSize: file.sizeBytes || dosya.size,
      storageDriver: file.storageDriver || "local",
      fileKey: resolvedFileKey,
      replyTo: replyTo
        ? { msgId: replyTo.id, id: replyTo.id, metin: replyTo.metin || "", ad: replyTo.ad || "", type: replyTo.type || "text", uid: replyTo.uid || null }
        : null,
      summary: ozet,
    }),
  });
  return publishMessageResponse(familyId, roomId, data, "added");
};

/* Ses mesajı gönder — type="audio", record alt klasörüne yükler */
export const sesGonderBackend = async ({ familyId, roomId, dosya, user, tamAd, durationSeconds = 0, replyTo = null, onProgress }) => {
  const formData = new FormData();
  formData.append("file", dosya);
  formData.append("type", "chat");
  formData.append("subType", "record");          // backend bu key'i okuyacak
  formData.append("familyId", familyId);
  formData.append("userId", user?.uid || "system");

  const upload = await apiUpload("/files/upload", formData, { onProgress });
  const file = upload.file || upload || {};
  const resolvedFileUrl = file.fileUrl || file.url || file.downloadUrl || upload.fileUrl || upload.url || upload.downloadUrl || "";
  const resolvedFileKey = file.fileKey || upload.fileKey || null;

  const data = await apiRequest(roomPath(familyId, roomId, "/messages"), {
    method: "POST",
    body: JSON.stringify({
      metin: "",
      uid: user?.uid,
      ad: tamAd,
      type: "audio",
      mediaUrl: resolvedFileUrl,
      fileUrl: resolvedFileUrl,
      mediaType: file.mimeType || dosya.type || "audio/webm",
      fileName: file.fileName || dosya.name,
      fileSize: file.sizeBytes || dosya.size,
      storageDriver: file.storageDriver || "local",
      fileKey: resolvedFileKey,
      replyTo: replyTo
        ? { msgId: replyTo.id, id: replyTo.id, metin: replyTo.metin || "", ad: replyTo.ad || "", type: replyTo.type || "text", uid: replyTo.uid || null }
        : null,
      summary: "🎤 Ses mesajı",
    }),
  });
  return publishMessageResponse(familyId, roomId, data, "added");
};

export const quickActionMesajService = async ({ familyId, roomId, user, tamAd, type, baslik }) => {
  const ikonlar = { task: "📋", shopping: "🛒", calendar: "📅" };
  const data = await apiRequest(roomPath(familyId, roomId, "/messages"), {
    method: "POST",
    body: JSON.stringify({
      metin: baslik,
      uid: user?.uid,
      ad: tamAd,
      type,
      summary: `${ikonlar[type] || ""} ${baslik}`.trim(),
    }),
  });
  return publishMessageResponse(familyId, roomId, data, "added");
};

export const mesajSilService = async ({ familyId, roomId, mesajId }) => {
  const data = await apiRequest(messagePath(familyId, roomId, mesajId), { method: "DELETE" });
  return publishMessageResponse(familyId, roomId, data);
};

export const mesajDuzenleService = async ({ familyId, roomId, mesajId, yeniMetin }) => {
  const data = await apiRequest(messagePath(familyId, roomId, mesajId), {
    method: "PATCH",
    body: JSON.stringify({ metin: yeniMetin?.trim(), edited: true }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

export const reaksiyonService = async ({ familyId, roomId, mesajId, emoji }) => {
  const data = await apiRequest(messagePath(familyId, roomId, mesajId, "/reaction"), {
    method: "POST",
    body: JSON.stringify({ emoji }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

export const mesajSabitleService = async ({ familyId, roomId, mesajId, pinned }) => {
  const data = await apiRequest(messagePath(familyId, roomId, mesajId), {
    method: "PATCH",
    body: JSON.stringify({ pinned: !pinned }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

// Eski istemci imzası korunur. Yeni ChatPage oda bazlı toplu read endpoint'ini
// kullanır; bu fonksiyon mağazadaki eski sürümlerle uyumluluk için kalır.
export const okunduIsaretle = async ({ familyId, roomId, mesajId, yenile = true }) => {
  try {
    const data = await apiRequest(messagePath(familyId, roomId, mesajId, "/read"), { method: "POST", body: JSON.stringify({}) });
    if (data?.changed && data?.message) publishMessageResponse(familyId, roomId, data, "read");
    else if (data?.changed && yenile) emitMessagesRefresh(familyId, roomId);
    return data;
  } catch (_) {}
};

// Toplu okundu isaretlemesi sonrasinda tek seferlik liste tazelemesi icin.
export const mesajlariYenileTetikle = (familyId, roomId) => {
  if (!familyId || !roomId) return;
  emitMessagesRefresh(familyId, roomId);
};

export const odayiOkunduYap = async ({ familyId, roomId, userUid = null, messageIds = [] }) => {
  if (!familyId || !roomId) return;

  const key = `${familyId}:${roomId}`;
  const now = Date.now();
  const previous = roomReadRequestState.get(key);
  const uniqueMessageIds = Array.from(new Set((messageIds || []).filter(Boolean)));
  const signature = uniqueMessageIds.map(String).sort().join(",");

  if (previous?.inFlight) {
    if (previous.signature === signature) return previous.inFlight;
    return previous.inFlight.finally(() => odayiOkunduYap({ familyId, roomId, userUid, messageIds: uniqueMessageIds }));
  }
  if (previous?.signature === signature && now - previous.sentAt < ROOM_READ_DEDUPE_MS) return;

  const request = apiRequest(roomPath(familyId, roomId, "/read"), {
    method: "POST",
    body: JSON.stringify({ messageIds: uniqueMessageIds }),
  })
    .then((data) => {
      if (data?.changed) {
        emitChatDataChanged(familyId, "chatMessages", "read", null, null, {
          roomId,
          room: data.room ? normaliseRoom(data.room) : null,
          messageIds: data.updatedMessageIds || [],
          userUid,
        });
      }
      return data;
    })
    .catch(() => {})
    .finally(() => {
      const latest = roomReadRequestState.get(key);
      if (latest?.inFlight === request) {
        roomReadRequestState.set(key, { signature: latest.signature, sentAt: latest.sentAt, inFlight: null });
      }
    });

  roomReadRequestState.set(key, { signature, sentAt: now, inFlight: request });
  return request;
};

export const menuOyVerService = async ({ familyId, roomId, mesaj, tip, userUid }) => {
  const oylar = mesaj?.menuVotes || {};
  const kullaniciOyu = Object.keys(oylar).find((key) => Array.isArray(oylar[key]) && oylar[key].includes(userUid));
  const yeniOylar = { ...oylar };

  if (kullaniciOyu === tip) {
    yeniOylar[tip] = (yeniOylar[tip] || []).filter((uid) => uid !== userUid);
  } else {
    if (kullaniciOyu) yeniOylar[kullaniciOyu] = (yeniOylar[kullaniciOyu] || []).filter((uid) => uid !== userUid);
    yeniOylar[tip] = Array.from(new Set([...(yeniOylar[tip] || []), userUid]));
  }

  Object.keys(yeniOylar).forEach((key) => {
    if (!Array.isArray(yeniOylar[key]) || yeniOylar[key].length === 0) delete yeniOylar[key];
  });

  const data = await apiRequest(messagePath(familyId, roomId, mesaj.id), {
    method: "PATCH",
    body: JSON.stringify({ menuVotes: yeniOylar }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

export const menuYenileService = async ({ familyId, roomId, mesajId, menuData }) => {
  const data = await apiRequest(messagePath(familyId, roomId, mesajId), {
    method: "PATCH",
    body: JSON.stringify({ menuData, menuVotes: {} }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

/* ════════════════════════════════════════════════════════════════════
   MADDE 4 — OYLAMA (POLL)

   Mevcut "Ne Pisirsem" menu kartinin oylama altyapisiyla BIREBIR ayni
   deseni kullanir: mesaj `type: "poll"` olarak gonderilir, oylar mesajin
   PATCH ucuyla guncellenir. Yeni endpoint veya ek bağlantı yoktur; oylar
   mevcut tek aile SSE bağlantısındaki chatMessages event'iyle gelir.
   ════════════════════════════════════════════════════════════════════ */

export const OYLAMA_MAX_SECENEK = 6;
export const OYLAMA_MIN_SECENEK = 2;

const oylamaSecenekTemizle = (secenekler = []) =>
  (Array.isArray(secenekler) ? secenekler : [])
    .map((s, i) => ({
      id: String(s?.id || `o${i + 1}`),
      metin: String(s?.metin ?? s ?? "").trim().slice(0, 120),
    }))
    .filter((s) => s.metin)
    .slice(0, OYLAMA_MAX_SECENEK);

export const oylamaGonderService = async ({
  familyId, roomId, user, tamAd,
  soru, secenekler, cokluSecim = false, anonim = false, replyTo = null,
}) => {
  const temizSoru = String(soru || "").trim().slice(0, 300);
  const temizSecenekler = oylamaSecenekTemizle(secenekler);
  if (!temizSoru || temizSecenekler.length < OYLAMA_MIN_SECENEK) {
    throw new Error("poll_invalid");
  }

  const pollData = {
    soru: temizSoru,
    secenekler: temizSecenekler,
    cokluSecim: !!cokluSecim,
    anonim: !!anonim,
    kapali: false,
    olusturanUid: user?.uid || null,
    olusturmaZamani: new Date().toISOString(),
  };

  return mesajGonderService({
    familyId,
    roomId,
    metin: "",
    user,
    tamAd,
    replyTo,
    ekstra: { type: "poll", pollData, pollVotes: {} },
  });
};

// Oy verme / oyu geri alma. Tekli secimde onceki oy otomatik dusurulur.
export const oylamaOyVerService = async ({ familyId, roomId, mesaj, secenekId, userUid }) => {
  const poll = mesaj?.pollData || {};
  if (poll.kapali) return normaliseMessage(mesaj || {});

  const mevcut = mesaj?.pollVotes || {};
  const yeniOylar = {};
  Object.keys(mevcut).forEach((key) => {
    yeniOylar[key] = Array.isArray(mevcut[key]) ? [...mevcut[key]] : [];
  });

  const zatenVerdi = (yeniOylar[secenekId] || []).includes(userUid);

  if (!poll.cokluSecim) {
    // Tekli secim: kullanicinin tum oylari temizlenir.
    Object.keys(yeniOylar).forEach((key) => {
      yeniOylar[key] = yeniOylar[key].filter((uid) => uid !== userUid);
    });
  } else if (zatenVerdi) {
    yeniOylar[secenekId] = (yeniOylar[secenekId] || []).filter((uid) => uid !== userUid);
  }

  if (!zatenVerdi) {
    yeniOylar[secenekId] = Array.from(new Set([...(yeniOylar[secenekId] || []), userUid]));
  }

  Object.keys(yeniOylar).forEach((key) => {
    if (!Array.isArray(yeniOylar[key]) || yeniOylar[key].length === 0) delete yeniOylar[key];
  });

  const data = await apiRequest(messagePath(familyId, roomId, mesaj.id), {
    method: "PATCH",
    body: JSON.stringify({ pollVotes: yeniOylar }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

// Oylamayi bitir (yalnizca olusturan veya aile yoneticisi cagirir).
export const oylamaKapatService = async ({ familyId, roomId, mesaj, kapali = true }) => {
  const yeniPoll = { ...(mesaj?.pollData || {}), kapali: !!kapali };
  const data = await apiRequest(messagePath(familyId, roomId, mesaj.id), {
    method: "PATCH",
    body: JSON.stringify({ pollData: yeniPoll }),
  });
  return publishMessageResponse(familyId, roomId, data);
};

export const odaAvatarGuncelleService = async ({ familyId, roomId, dosya, user, onProgress }) => {
  const formData = new FormData();
  formData.append("file", dosya);
  formData.append("type", "chat");
  formData.append("subType", "room-avatar");
  formData.append("familyId", familyId);
  formData.append("userId", user?.uid || "system");

  const upload = await apiUpload("/files/upload", formData, { onProgress });
  const file = upload.file || upload || {};
  const data = await apiRequest(roomPath(familyId, roomId), {
    method: "PATCH",
    body: JSON.stringify({
      avatarUrl: file.fileUrl || file.url || upload.fileUrl || upload.url || "",
      avatarFileKey: file.fileKey || upload.fileKey || null,
    }),
  });
  const room = normaliseRoom(data.room || {});
  emitChatDataChanged(familyId, "chatRooms", "updated", room, room.id);
  return room;
};

export const odaAyarGuncelleService = async ({ familyId, roomId, patch }) => {
  const data = await apiRequest(roomPath(familyId, roomId), {
    method: "PATCH",
    body: JSON.stringify(patch || {}),
  });
  const room = normaliseRoom(data.room || {});
  emitChatDataChanged(familyId, "chatRooms", "updated", room, room.id);
  return room;
};

export const odayaUyeCikarService = async ({ familyId, roomId, uyeUid }) => {
  const data = await apiRequest(roomPath(familyId, roomId, `/members/${encodeURIComponent(uyeUid)}`), { method: "DELETE" });
  if (data?.room) emitChatDataChanged(familyId, "chatRooms", "updated", normaliseRoom(data.room), roomId);
};


export const otomatikGaleriKayitKontrolService = async ({ familyId, roomId, mesajId }) => {
  if (!familyId || !roomId || !mesajId) return { shouldSave: false };
  return apiRequest(messagePath(familyId, roomId, mesajId, "/autosave-photo"), {
    method: "POST",
    body: JSON.stringify({ action: "check" }),
  });
};

export const otomatikGaleriKayitIsaretleService = async ({ familyId, roomId, mesajId }) => {
  if (!familyId || !roomId || !mesajId) return { ok: false };
  return apiRequest(messagePath(familyId, roomId, mesajId, "/autosave-photo"), {
    method: "POST",
    body: JSON.stringify({ action: "mark" }),
  });
};

// Geriye dönük uyumluluk: eski isim artık sadece kayıt durumunu kontrol eder.
export const otomatikGaleriKayitAyirService = otomatikGaleriKayitKontrolService;

export const aktifChatSessionService = async ({ familyId, active, roomId = null }) => {
  if (!familyId) return;

  const payload = {
    active: !!active,
    roomId: active ? roomId : null,
    page: active ? "chat" : null,
  };
  const signature = JSON.stringify(payload);
  const now = Date.now();
  const previous = activeSessionRequestState.get(familyId);

  if (previous?.signature === signature) {
    if (previous.inFlight) return previous.inFlight;
    if (now - previous.sentAt < ACTIVE_SESSION_DEDUPE_MS) return;
  }

  const request = apiRequest(chatPath(familyId, "/active-session"), {
    method: "POST",
    body: JSON.stringify(payload),
  }).catch(() => {}).finally(() => {
    const latest = activeSessionRequestState.get(familyId);
    if (latest?.inFlight === request) {
      activeSessionRequestState.set(familyId, {
        signature: latest.signature,
        sentAt: latest.sentAt,
        inFlight: null,
      });
    }
  });

  activeSessionRequestState.set(familyId, { signature, sentAt: now, inFlight: request });
  return request;
};
